import { ErrorHandler, inject, Injectable, Injector } from '@angular/core';
import { LoggingService } from '../../logging/logging.service';
import { HttpErrorResponse } from '@angular/common/http';
import { CurafidaAuthService } from '../../authentication/curafida-auth.service';
import { isBackendRequest } from '../../common/services/interceptor/is-backend-request';

@Injectable()
export class GlobalErrorHandler implements ErrorHandler {
    protected readonly log = inject(LoggingService).getLogger(this.constructor.name);
    /*
     * Resolved lazily on purpose - do NOT turn this into `inject(CurafidaAuthService)`.
     *
     * Angular builds the `ErrorHandler` while it resolves the environment initializers, which is
     * before any `APP_INITIALIZER` has run. `CurafidaAuthService`'s constructor throws when the
     * frontend configuration has not been fetched yet, and a throw at that point kills the bootstrap
     * while the error handler that should report it is still half-built. `{ optional: true }` would
     * not help either: it only swallows a missing provider, not a failing constructor.
     */
    private readonly injector = inject(Injector);

    handleError(error: any): void {
        const chunkFailedMessage = /Loading chunk [\d]+ failed/;

        if (chunkFailedMessage.test(error.message)) {
            window.location.reload();
        }
        if (error instanceof HttpErrorResponse) {
            //Backend returns unsuccessful response codes such as 404, 500 etc.
            this.log.error('Backend returned status code: ', error.status);
            this.log.error('Response body:', error.message);
            if (error.status === 401) {
                void this.recoverFromUnauthorized(error);
                return;
            }
        } else {
            //A client-side or network error occurred.
            return this.log.error('An error occurred:', error);
        }
        this.log.error(error);
    }

    /**
     * Asks `CurafidaAuthService` for a fresh token after an unhandled 401.
     *
     * The refresh deliberately goes through the auth service rather than `OAuthService` directly:
     * the auth service rate limits refreshes, so a page whose requests all answer 401 at once
     * triggers a single call to Keycloak instead of one per failed request.
     *
     * No branch navigates. A successful refresh leaves the user on the page they were on, and a
     * failed one is already being handled by the auth service, which owns the decision of whether
     * the session is recoverable and logs out when it is not.
     */
    private async recoverFromUnauthorized(error: HttpErrorResponse): Promise<void> {
        try {
            // Only the path, so that a token or a patient id in the query string stays out of the logs.
            const reason = `401 on ${this.pathOf(error.url)}`;
            /*
             * Public endpoints - Keycloak, Directus, the anonymous webshop - never carried our token,
             * so refreshing it cannot turn their 401 into a 200. Pages such as /register and /shop are
             * reachable without a login, and a refresh there would end in a logout of a visitor who
             * never had a session.
             */
            if (error.url && !isBackendRequest(error.url)) {
                this.log.debug(`[recoverFromUnauthorized] ${reason} -> public endpoint, no refresh`);
                return;
            }
            const authService = this.injector.get(CurafidaAuthService);
            const outcome = await authService.tryRefreshToken(reason);
            if (outcome === 'failed') {
                this.log.error(`[recoverFromUnauthorized] ${reason} -> refresh failed, auth service takes over`);
                return;
            }
            this.log.debug(`[recoverFromUnauthorized] ${reason} -> ${outcome}`);
        } catch (recoverError) {
            /*
             * Nothing may escape here: this runs detached from `handleError`, so a rejection would
             * surface as an unhandled one and be routed back into this very error handler.
             */
            this.log.error('[recoverFromUnauthorized] unexpected failure', recoverError);
        }
    }

    private pathOf(url: string | null): string {
        if (!url) return 'unknown url';
        try {
            return new URL(url, window.location.origin).pathname;
        } catch {
            return 'unparsable url';
        }
    }
}
